import styled from '@emotion/styled';
import {
  Card as _Card,
  CardActionArea as _CardActionArea,
  CardContent as _CardContent,
  CardHeader as _CardHeader,
  Typography,
} from '@material-ui/core';
import { ClassAttributes, createElement, MouseEventHandler } from 'react';
import { FormattedDate, FormattedMessage } from 'react-intl';

import { SingleCreationForDetailFragment } from '../../../../graphql/generated/graphql';
import { getLatestDeliverable } from '../../../../utility/graphql/creation';
import { toJSDate } from '../../../../utility/graphql/datetime';
import AvatarAndName from '../../../component/compound/AvatarAndName';
import MediaPlayer from '../../../component/compound/MediaPlayer';
import AspectRatioFixed from '../../../component/primitive/AspectRatioFixed';
import { SPACING_UNIT } from '../../../constant/spacing';
import { gridLayoutItem } from '../../../styleUtility/gridLayout';
import CreationsCardHeader from './CreationsCardHeader';

export type Props = ClassAttributes<HTMLElement> & {
  creation: Pick<SingleCreationForDetailFragment, 'id' | 'deliverables' | 'assignee' | 'creativeFormat'>;
  className?: string;
  onClickSome?: MouseEventHandler<HTMLElement>;
  onClickProfile?: (url: string) => void;
  profileWebUrl?: string;
};

const CreationsItem = ({ creation, className, onClickSome, onClickProfile, profileWebUrl }: Props) => {
  const latestDeliverable = getLatestDeliverable(creation.deliverables);
  return (
    <Card className={className} style={{ width: '100%' }}>
      {onClickSome ? (
        <CardActionArea component="div" onClick={onClickSome}>
          <CardHeader subheader={<CreationsCardHeader creation={creation} />} />
        </CardActionArea>
      ) : onClickProfile ? (
        <CardActionArea
          component="div"
          onClick={() => {
            onClickProfile(`${profileWebUrl}/users/${creation.assignee!.id}`);
          }}
        >
          <CardHeader subheader={<CreationsCardHeader creation={creation} />} />
        </CardActionArea>
      ) : (
        <CardContent>
          <CardHeader subheader={<CreationsCardHeader creation={creation} />} />
        </CardContent>
      )}
      <VideoOrImage aspectRatio={[1, 1]}>
        <MediaPlayer mimeType={latestDeliverable!.creative.file.mimeType} url={latestDeliverable!.creative.file.url} />
      </VideoOrImage>
      <CardFooter>
        <User>
          <AvatarAndName
            user={{
              profileImageUrl: creation.assignee!.profileImageUrl,
              name: creation.assignee!.name,
            }}
          />
        </User>
        <CreatedAt>
          <Typography variant="caption" color="textSecondary">
            <FormattedDate
              year="numeric"
              month="2-digit"
              day="2-digit"
              hour="2-digit"
              minute="2-digit"
              value={toJSDate(latestDeliverable!.createdAt)}
            >
              {(date: string) => <FormattedMessage id="creations.creations_item.created_at" values={{ date }} />}
            </FormattedDate>
          </Typography>
        </CreatedAt>
      </CardFooter>
    </Card>
  );
};

const Card = styled(_Card)({
  width: '100%',
});

const CardHeader = styled(_CardHeader)({
  width: '100%',
});

const CardActionArea = (styled(_CardActionArea)({
  width: '100%',
}) as unknown) as typeof _CardActionArea;

const CardContent = styled(_CardContent)({
  width: '100%',
  padding: 0,
});

const CardFooter = styled(_CardContent)({
  ...gridLayoutItem('content'),
  padding: `${SPACING_UNIT}px ${SPACING_UNIT * 2}px`,
});

const User = styled(Typography)({
  padding: SPACING_UNIT,
});

const CreatedAt = styled(Typography)({
  paddingLeft: SPACING_UNIT,
});

const VideoOrImage = styled(AspectRatioFixed)({});

export default CreationsItem;
